import { defineStore } from 'pinia';
import * as uuid from 'uuid';

import type {
  DrawLayerBBoxDTO,
  DrawLayerCreatePayload,
  DrawLayerGeometryPatchDTO,
  DrawLayerResponseDTO,
  DrawLayerTilesResponseDTO,
  DrawLayerUpdateMetadataPayload,
  GeoJsonFeatureCollectionDTO,
  NamedLatLonCoordinateDTO,
} from '@nora-monorepo/nora-api-client';
import type { KnownDrawLayerGeneration, MapTile } from '@nora-monorepo/shared';
import { isNonEmptyString } from '@nora-monorepo/shared';
import {
  appLogger,
  useApiStore,
  useGenericUserStore,
  UserLoggingContext,
} from '@nora-monorepo/ui-shared';

export const useDrawLayerApiStore = defineStore('drawLayerApiStore', () => {
  const api = useApiStore();
  const userStore = useGenericUserStore();

  async function createDrawLayer(payload: DrawLayerCreatePayload, operationId: string): Promise<DrawLayerResponseDTO | null> {
    const drawLayerId = payload.id ?? uuid.v4();

    try {
      const newDrawLayer = await api.unwrap(
        api.drawLayerOperation.createDrawLayer({
          operationId,
          drawLayerCreatePayload: {
            ...payload,
            id: drawLayerId,
            features: payload.features ?? undefined,
          },
        }),
      );

      if (newDrawLayer?.data) {
        return {
          id: drawLayerId,
          name: payload.name,
          locked: false,
          draft: payload.draft ?? false,
          isDefault: false,
          operationId,
          // Server-computed fields
          type: newDrawLayer.data.type,
          permissions: newDrawLayer.data.permissions,
          geoJson: newDrawLayer.data.geoJson,
        };
      }

      throw new Error('No new draw layer created');
    }
    catch (error) {
      console.error('Error saving draw layer', error);

      return null;
    }
  }

  async function getDrawLayersByOperationId(operationId: string): Promise<DrawLayerResponseDTO[] | null> {
    try {
      const drawLayers = await api.unwrap(
        api.drawLayerOperation.getDrawLayersByOperationId({
          operationId,
        }),
      );

      if (!drawLayers?.data) {
        throw new Error('No draw layers found for operation');
      }

      return drawLayers.data;
    }
    catch (error) {
      appLogger.error({
        message: 'Error fetching draw layers for operation',
        context: {
          operationId,
          user: new UserLoggingContext({ userId: userStore.user?.id ?? 'unknown', username: userStore.user?.username ?? 'unknown' }),
        },
        sensitiveContext: {
          error: {
            message: error instanceof Error ? error.message : 'Unknown error',
            name: error instanceof Error ? error.name : 'Unknown error',
            stack: error instanceof Error ? error.stack : 'No stack trace',
          },
        },
      });

      return null;
    }
  }

  async function publishDrawLayer(drawLayerId: string, operationId: string): Promise<DrawLayerResponseDTO | null> {
    try {
      const publishedDrawLayer = await api.unwrap(
        api.drawLayerOperation.publishDrawLayer({
          id: drawLayerId,
          operationId,
        }),
      );

      if (!publishedDrawLayer?.data) {
        throw new Error('No published draw layer returned');
      }

      return publishedDrawLayer.data;
    }
    catch (error) {
      console.error('Error publishing draw layer', error);

      return null;
    }
  }

  async function moveOperationDrawLayerToOperation(drawLayerId: string, fromOperationId: string, toOperationId: string): Promise<void> {
    try {
      await api.unwrap(
        api.drawLayerOperation.moveOperationDrawLayer({
          id: drawLayerId,
          operationId: fromOperationId,
          moveDrawLayerPayload: {
            operationId: toOperationId,
          },
        }),
      );
    }
    catch (error) {
      console.error('Error moving operation draw layer to another operation', error);
    }
  }

  async function copyOperationDrawLayerToOperation(drawLayerId: string, fromOperationId: string, toOperationId: string, newDrawLayerId?: string): Promise<void> {
    if (!isNonEmptyString(newDrawLayerId)) {
      newDrawLayerId = uuid.v4();
    }

    try {
      await api.unwrap(
        api.drawLayerOperation.copyOperationDrawLayer({
          id: drawLayerId,
          operationId: fromOperationId,
          copyDrawLayerPayload: {
            newDrawLayerId,
            operationId: toOperationId,
          },
        }),
      );
    }
    catch (error) {
      console.error('Error copying operation draw layer to another operation', error);
    }
  }

  async function updateOperationDrawLayerGeometry(drawLayerId: string, operationId: string, patch: DrawLayerGeometryPatchDTO): Promise<void> {
    try {
      await api.unwrap(
        api.drawLayerOperation.patchDrawLayerGeometry({
          id: drawLayerId,
          operationId,
          drawLayerGeometryPatchDTO: patch,
        }),
      );
    }
    catch (error) {
      console.error('Error updating draw layer geometry', error);
    }
  }

  async function updateDrawLayerMetadata(drawLayerId: string, operationId: string, update: DrawLayerUpdateMetadataPayload): Promise<void> {
    try {
      const updatedDrawLayer = await api.unwrap(
        api.drawLayerOperation.updateDrawLayerMetaData({
          id: drawLayerId,
          operationId,
          drawLayerUpdateMetadataPayload: update,
        }),
      );

      if (!updatedDrawLayer?.data) {
        throw new Error('Failed to update draw layer metadata');
      }
    }
    catch (error) {
      console.error('Error updating draw layer metadata', error);
    }
  }

  async function deleteOperationDrawLayer(drawLayerId: string, operationId: string): Promise<void> {
    try {
      await api.unwrap(
        api.drawLayerOperation.deleteDrawLayer({
          id: drawLayerId,
          operationId,
        }),
      );
    }
    catch (error) {
      console.error('Error deleting draw layer', error);
    }
  }

  /**
   * Fetches the features of a batch of map tiles (at most FEATURE_TILE_BATCH_MAX, cached and
   * not). Cached tiles only come back for draw layers whose generation is not the known one.
   * @returns The draw layers' generations and the tiles to (re)load, or null when the request failed
   */
  async function getTilesFeatures(
    tiles: MapTile[],
    cachedTiles: MapTile[],
    operationId: string,
    drawLayers: KnownDrawLayerGeneration[],
  ): Promise<DrawLayerTilesResponseDTO | null> {
    try {
      const response = await api.unwrap(
        api.drawLayer.getDrawLayerFeaturesByTiles({
          tiles: tiles.map(tile => tile.key),
          cachedTiles: cachedTiles.map(tile => tile.key),
          operationId,
          drawLayers: drawLayers.map(drawLayer => drawLayer.entry),
        }),
      );

      if (!response?.data) {
        throw new Error('Failed to get tile features');
      }

      return response.data;
    }
    catch (error) {
      console.error(`Error getting features for tiles ${[...tiles, ...cachedTiles].map(tile => tile.key).join(', ')}`, error);

      return null;
    }
  }

  /**
   * Reloads only the changed IDs; an empty collection means they were deleted.
   * @returns The authoritative features, or null when the request failed
   */
  async function getDrawLayerFeatures(drawLayerId: string, operationId: string, featureIds: string[]): Promise<GeoJsonFeatureCollectionDTO | null> {
    try {
      const response = await api.unwrap(
        api.drawLayerOperation.getDrawLayerFeatures({ id: drawLayerId, operationId, featureIds }),
      );

      if (!response?.data) {
        throw new Error('Failed to get draw layer features');
      }

      return response.data;
    }
    catch (error) {
      appLogger.error({
        message: 'Error getting draw layer features',
        context: { drawLayerId, operationId },
        sensitiveContext: { error },
      });

      return null;
    }
  }

  async function updateOperationDrawLayerMetadata(drawLayerId: string, operationId: string, update: DrawLayerUpdateMetadataPayload): Promise<void> {
    try {
      await api.unwrap(
        api.drawLayerOperation.updateDrawLayerMetaData({
          id: drawLayerId,
          operationId,
          drawLayerUpdateMetadataPayload: update,
        }),
      );
    }
    catch (error) {
      console.error('Error updating operation draw layer metadata', error);
    }
  }

  async function unlockOperationDrawLayer(drawLayerId: string, operationId: string): Promise<void> {
    await updateOperationDrawLayerMetadata(drawLayerId, operationId, { locked: false });
  }

  async function lockOperationDrawLayer(drawLayerId: string, operationId: string): Promise<void> {
    await updateOperationDrawLayerMetadata(drawLayerId, operationId, { locked: true });
  }

  async function setDefaultDrawLayer(drawLayerId: string, operationId: string): Promise<boolean> {
    try {
      await api.unwrap(
        api.nexusOperation.setDefaultLayer({
          operationId,
          operationChangeDefaultDrawLayerDTO: { drawLayerId },
        }),
      );

      return true;
    }
    catch (error) {
      console.error('Error setting default draw layer', error);

      return false;
    }
  }

  async function getOperationDrawLayerBBox(drawLayerId: string, operationId: string, additionalCoordinates?: NamedLatLonCoordinateDTO[]): Promise<DrawLayerBBoxDTO | null> {
    try {
      const bbox = await api.unwrap(
        api.drawLayerOperation.calculateDrawLayerBBox({
          id: drawLayerId,
          operationId,
          additionalCoordinates,
        }),
      );

      if (!bbox?.data) {
        throw new Error('No bbox returned for operation draw layer');
      }

      return bbox.data;
    }
    catch (error) {
      console.error('Error getting operation draw layer bbox', error);

      return null;
    }
  }

  async function getOperationDrawLayerGeoJsonById(drawLayerId: string, operationId: string): Promise<GeoJsonFeatureCollectionDTO | null> {
    try {
      const geoJson = await api.unwrap(
        api.drawLayerOperation.getDrawLayerGeoJson({
          id: drawLayerId,
          operationId,
        }),
      );

      if (!geoJson?.data) {
        throw new Error('No GeoJSON returned for operation draw layer');
      }

      return geoJson.data;
    }
    catch (error) {
      console.error('Error getting operation draw layer GeoJSON', error);

      return null;
    }
  }

  return {
    createDrawLayer,
    getDrawLayersByOperationId,
    publishDrawLayer,
    updateOperationDrawLayerGeometry,
    updateDrawLayerMetadata,
    deleteOperationDrawLayer,
    getTilesFeatures,
    getDrawLayerFeatures,
    unlockOperationDrawLayer,
    lockOperationDrawLayer,
    setDefaultDrawLayer,
    getOperationDrawLayerBBox,
    getOperationDrawLayerGeoJsonById,
    moveOperationDrawLayerToOperation,
    copyOperationDrawLayerToOperation,
  };
});
